import React, { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container, Grid, Card, CardContent, CardActions, CardMedia, Typography,
  Button, Chip, Box, ToggleButton, ToggleButtonGroup, Stack, Tooltip
} from '@mui/material';
import { Accessibility, Code, DesignServices, CallMade, Article } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { projects } from '../data/projects';
import { designProjects } from '../data/designprojects';
import {styled} from '@mui/material/styles'

const allProjects = [...projects, ...designProjects]

const StyledCard = styled(Card)(({ theme }) => ({
  //height: 450,
  borderRadius: 4,
  border: '0.25px solid rgba(191, 155, 122, 0.26)',
  background: `
    linear-gradient(
      160deg,
      rgba(43, 37, 33, 0.95) 0%,
      rgba(16, 12, 9, 0.98) 50%,
      rgba(20, 14, 10, 1.0) 100%
    )
  `,
  boxShadow: `
    0 0 0 1px rgba(191, 155, 122, 0.08),
    0 4px 24px rgba(83, 83, 83, 0.53),
    0 1px 0 rgba(191, 155, 122, 0.15) inset
  `,
  color: 'text.primary',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  padding: theme.spacing(3),
  transition: 'transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1), border-color 0.3s ease, box-shadow 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent 0%, #CD7F18 50%, transparent 100%)',
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(ellipse at 50% 0%, rgba(205, 127, 24, 0.04) 0%, transparent 60%)',
    opacity: 0,
    transition: 'opacity 0.4s ease',
    pointerEvents: 'none',
  },
 
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: 'rgba(191, 155, 122, 0.28)',
    boxShadow: `
      0 0 0 1px rgba(191, 155, 122, 0.2),
      0 12px 40px rgba(0, 0, 0, 0.7),
      0 1px 0 rgba(191, 155, 122, 0.25) inset
    `,
    '&::before': { opacity: 1 },
    '&::after': { opacity: 1 },
  },
}));

export default function PortfolioGrid() {
  const navigate = useNavigate();
   const {id} = useParams()
  const project = allProjects.find((project) => project.id === id)
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTags, setSelectedTags] = useState([]);

  const categoryColors = {
    audit: '#18C9CD',
    design: '#8C3220',
    code: '#CD7F18',
    writing: 'rgb(55, 0, 114)'
  };

  const categoryIcons = {
    audit: <Accessibility />,
    design: <DesignServices />,
    code: <Code />,
    writing: <Article />
  };

//   const allTags = useMemo(() => {
//     const tags = new Set();
//     projects.forEach(project => {
//       project.badges?.forEach(badge => tags.add(badge.label));
//       project.methods?.forEach(method => tags.add(method));
//     });
//     return Array.from(tags).sort();
//   }, [projects]);

  // Filter projects
  const filteredProjects = useMemo(() => {
  let filtered = allProjects;

  if (selectedCategory !== 'all') {
    filtered = filtered.filter(project => project.category.includes(selectedCategory));
  }

  if (selectedTags.length > 0) {
    filtered = filtered.filter(project => {
      const projectTags = [
        ...(project.badges?.map(b => b.label) || []),
        ...(project.methods || [])
      ];
      return selectedTags.some(tag => projectTags.includes(tag));
    });
  }

  return filtered;
}, [selectedCategory, selectedTags]);

  const handleCategoryChange = (event, newCategory) => {
    if (newCategory !== null) {
      setSelectedCategory(newCategory);
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleViewProject = (projectId) => {
    navigate(`/project/${projectId}`);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Development • Interactive Systems • Design Engineering
        </Typography>
        <Typography variant="body1" color="#ccc" sx={{ mt: 2 }}>
          Building interactive systems that combine thoughtful UX, scalable frontend architecture, and visual precision.
        </Typography>
        {/* Stats Bar */}
        <Stack
          direction="row"
          spacing={4}
          justifyContent="center"
          sx={{ mt: 4, flexWrap: 'wrap' }}
        >
        </Stack>
      </Box>

      {/* Category Filter */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center'}}>
        <ToggleButtonGroup
          value={selectedCategory}
          exclusive
          onChange={handleCategoryChange}
          aria-label="project category filter"
          sx={{
            '& .MuiToggleButton-root': {
              color: 'primary.light',
              borderColor: 'primary.light',
              // '&.Mui-selected': {
              //   color: 'categoryColors.selectedCategory',
              //   fontWeight: 'bold'
              // }
            },
            flexDirection: {xs: 'column', sm: 'row '}
          }}
        >
          <ToggleButton value="all" aria-label="all projects">
            All Work
          </ToggleButton>
          {/* <ToggleButton
            value="audit"
            aria-label="accessibility audits"
            sx={{
              '&.Mui-selected': {
                bgcolor: `${categoryColors.audit}40`,
                borderColor: categoryColors.audit
              }
            }}
          >
            {categoryIcons.audit}
            <Box component="span" sx={{ ml: 1 }}>Accessibility Audits</Box>
          </ToggleButton> */}
          <ToggleButton
            value="code"
            aria-label="code projects"
            sx={{
              '&.Mui-selected': {
                bgcolor: `${categoryColors.code}40`,
                borderColor: categoryColors.code
              }
            }}
          >
            {categoryIcons.code}
            <Box component="span" sx={{ ml: 1 }}>Projects</Box>
          </ToggleButton>
          <ToggleButton
            value="design"
            aria-label="product design"
            sx={{
              '&.Mui-selected': {
                bgcolor: `${categoryColors.design}40`,
                borderColor: categoryColors.design
              }
            }}
          >
            {categoryIcons.product}
            <Box component="span" sx={{ ml: 1 }}>Design & Research</Box>
          </ToggleButton>
          <ToggleButton
            value='writing'
            aria-label='articles written'
            sx={{
              '&.Mui-selected': {
                bgcolor: `${categoryColors.writing}40`,
                borderColor: categoryColors.writing
              }
            }}
          >
            {categoryIcons.writing}
             <Box component="span" sx={{ ml: 1 }}>Writing</Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {/* Tag Filter */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="body1" color="accent.main" gutterBottom textAlign="center">
          Click to filter by skills & tools:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {['Vue.js', 'React', 'Figma', 'Design', 'API Integration'].map(tag => (
            <Chip
              key={tag}
              label={tag}
              onClick={() => handleTagToggle(tag)}
              variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
              color={selectedTags.includes(tag) ? 'primary' : 'default'}
              sx={{
                cursor: 'pointer',
                borderColor: selectedTags.includes(tag) ? '#841717' : '#D4B896',
                color: selectedTags.includes(tag) ? 'text.primary' : 'text.secondary',
                bgcolor: selectedTags.includes(tag) ? 'cta.main' : 'transparent'
              }}
            />
          ))}
        </Box>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size='small'
            onClick={() => {
              setSelectedCategory('all');
              setSelectedTags([]);
            }}
            sx={{ mt: 2, borderColor: '', color: 'primary.contrastText' }}
          >
            Clear All Filters
          </Button>
        </Box>
      </Box>

      {/* Results Count */}
      <Typography variant="body2" color="#ccc" sx={{ mb: 2, textAlign: 'center' }}>
        Showing {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
      </Typography>

      {/* Project Grid */}
      <Grid container spacing={4}>
        {filteredProjects.map((project) => {
            const categoryColor = categoryColors[project.category] || '#18C9CD';

            return (
            <Grid item xs={12} sm={6} md={4} key={project.id} sx={{ display: 'flex' }}>
                <StyledCard>
                {/* Image */}
                <CardMedia
                    component="img"
                    image={project.image}
                    alt={`Image for ${project.name}`}
                    sx={{
                    height: 200,
                    borderRadius: '6px',
                    objectFit: 'cover',
                    }}
                />
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                    <Typography variant="overline" gutterBottom>
                    {project.name}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, mt: 2 }}>
                    {project.project}
                    </Typography>

                    {/* Badges (colored by category) */}
                    <Grid container spacing={1} justifyContent="center" sx={{ mb: 1 }}>
                    {project.label?.slice(0, 4).map((tag, idx) => (
                        <Grid item key={idx}>
                          <Chip
                              label={tag}
                              size="small"
                              sx={{
                              bgcolor: `${categoryColor}20`,
                              color: 'text.secondary',
                              border: `1px solid ${categoryColor}`,
                              fontWeight: 500,
                              cursor: 'pointer'
                              }}
                          />
                        </Grid>
                    ))}
                    {project.label && project.label.length > 4 && (
                        <Grid item>
                          <Tooltip title={project.label.slice(4).join(', ')}>
                            <Chip
                              label={`+${project.label.length - 4}`}
                              size="small"
                              sx={{
                              bgcolor: '#222',
                              color: 'text.primary',
                              border: `1px solid ${categoryColor}`,
                              fontWeight: 500,
                              }}
                          />
                          </Tooltip>
                          
                        </Grid>
                    )}
                    </Grid>
                </CardContent>
                {/* Buttons */}
                <CardActions sx={{ justifyContent: 'center' }}>
                    {(project.url || project.internalRoute) && (
                    <Button
                        variant="outlined"
                        {...(project.internalRoute
                          ? { component: Link, to: project.internalRoute }
                          : { href: project.url, target: '_blank', rel: 'noopener noreferrer',}
                        )}
                        // href={project.url}
                        sx={{
                        color: 'primary.light',
                        borderColor: 'cta.main',
                        '&:hover': { bgcolor: `${categoryColor}20` },
                        }}
                    >
                        {project.navigation || 'Live Site'}
                    </Button>
                    )}
                    <Button
                    variant="outlined"
                    component={Link}
                    to={`/projects/${project.id}`}
                    endIcon={<CallMade />}
                    sx={{
                        color: 'primary.light',
                        borderColor: 'cta.main',
                        '&:hover': { bgcolor: `${categoryColor}20` },
                    }}
                    >
                    Case Study
                    </Button>
                </CardActions>
                </StyledCard>
            </Grid>
            );
        })}
        </Grid>

      {/* No Results Message */}
      {filteredProjects.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Typography variant="h5" color="#ccc" gutterBottom>
            No projects match your filters
          </Typography>
          <Typography variant="body1" color="#999">
            Try adjusting your category or tag selections
          </Typography>
          <Button
            variant="contained"
            size='large'
            onClick={() => {
              setSelectedCategory('all');
              setSelectedTags([]);
            }}
            sx={{ mt: 2, backgroundColor: 'warning.main', color: 'text.primary' }}
          >
            Clear All Filters
          </Button>
        </Box>
      )}

      {/* Call to Action */}
      <Box sx={{ mt: 8, textAlign: 'center', p: 4, borderRadius: 2 }}>
        {/* <Typography variant="h5" gutterBottom color="#fff">
          Currently Pursuing WAS & CPACC Certification
        </Typography> */}
        <Typography variant="body1" color="#ccc" sx={{ mb: 3 }}>
          Passionate about building inclusive experiences that work for everyone
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            component='a'
            href='mailto:jlindsey010@gmail.com'
            sx={{ bgcolor: 'cta.main', '&:hover': { bgcolor: 'primary.light', opacity: 0.9 } }}
          >
            Contact Me
          </Button>
          <Button
            variant="outlined"
            component='a'
            href={`${process.env.PUBLIC_URL}/Jennifer_Lindsey_Resume.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderColor: 'cta.main', color: 'cta.main', '&:hover': { bgcolor: 'primary.light', opacity: 0.9 } }}
          >
            Download Resume
          </Button>
        </Stack>
      </Box>
    </Container>
  )
}